import * as React from 'react';

import {
  Badge,
  Button,
  FloatingToolbar,
  Row,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideTrash2, LucideX } from 'lucide-react';

import { billVoidAtom } from '../../_graphql/mutationBillVoid';
import * as state from '../../_state/financeBills.state';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { BillRecord } from '~/modules/finance/types';

//
// ⚛️ ⚛️ ⚛️ BILLS LIST FLOATING TOOLBAR ⚛️ ⚛️ ⚛️
//
export const BillsListFloatingToolbar = React.memo(() => {
  // Translations
  const t = useTranslations();

  // Atoms
  const { data: { bills } = { bills: [] }, refetch: refetchBills } =
    useAtomValue(state.$remoteBills);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const setBillsToPay = useSetAtom(state.$billsToPay);
  const setIsPaymentDrawerOpen = useSetAtom(state.$isPaymentDrawerOpen);

  // Memoized values
  const $voidBill = React.useMemo(
    () =>
      billVoidAtom({
        onSuccess: () => {
          refetchBills?.();
          setSelectedKeys(new Set());
        },
      }),
    [refetchBills, setSelectedKeys],
  );
  const voidBill = useSetAtom($voidBill);

  // Data hooks
  const toast = useToast();
  const selectedRows = useMemoDataGridRows(bills, selectedKeys);

  // Callbacks
  const handlePay = React.useCallback(
    (billsToPaySelected: BillRecord[]) => {
      const allPending = billsToPaySelected.every(
        (bill) => bill.status === 'Pending',
      );
      if (!allPending) {
        toast.error(t.bills.list.tooltips.notAllPending);
        return;
      }
      setBillsToPay(billsToPaySelected);
      setIsPaymentDrawerOpen(true);
    },
    [toast, t, setBillsToPay, setIsPaymentDrawerOpen],
  );

  const handleBatchVoid = React.useCallback(
    async (billsToVoid: BillRecord[]) => {
      const canVoid = billsToVoid.every((bill) => bill.status === 'Pending');
      if (!canVoid) {
        toast.error(t.bills.list.tooltips.cannotVoid);
        return;
      }
      let succeeded = 0;
      for (const bill of billsToVoid) {
        try {
          await voidBill({ id: bill.id });
          succeeded++;
        } catch (e) {
          console.error(e);
        }
      }
      if (succeeded === billsToVoid.length) {
        toast.success(t.bills.list.toolbar.voidSuccess(succeeded));
      } else if (succeeded > 0) {
        toast.warning(
          t.bills.list.toolbar.voidPartial(succeeded, billsToVoid.length),
        );
      } else {
        toast.error(t.bills.list.toolbar.voidFailed);
      }
    },
    [voidBill, toast, t],
  );

  return (
    <FloatingToolbar visible={selectedRows.length > 0}>
      <Row xBetween gap3>
        <Row gap2 className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>
          {t.bills.list.toolbar.selected}
        </Row>
        <Row gap2>
          <Button
            variant="solid"
            color="success"
            onPress={() => handlePay(selectedRows)}
          >
            {t.bills.list.toolbar.pay(selectedRows.length)}
          </Button>
          <Button
            variant="outline"
            color="failure"
            icon={<LucideTrash2 className="size-4" />}
            onPress={() => handleBatchVoid(selectedRows)}
          >
            {t.bills.list.toolbar.void(selectedRows.length)}
          </Button>
        </Row>
        <LucideX
          className="size-4 cursor-pointer text-neutral/80"
          onClick={() => setSelectedKeys(new Set())}
        />
      </Row>
    </FloatingToolbar>
  );
});
